// src/pages/modals/PaymentModal.js
import React, { useEffect, useCallback, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Stack,
  Chip,
  Divider,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PaymentIcon from '@mui/icons-material/Payment';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

import '../css/POS.css';

const bills = [
  { value: 1000, img: '/assets/b1000.jpg' },
  { value: 2000, img: '/assets/b2000.jpg' },
  { value: 5000, img: '/assets/b5000.jpg' },
  { value: 10000, img: '/assets/b10000.jpg' },
  { value: 20000, img: '/assets/b20000.jpg' },
];

const fmtCLP = (n) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(Number(n || 0)));

function roundCashLegCLP(value) {
  const amount = Math.round(Number(value) || 0);
  const sign = amount < 0 ? -1 : 1;
  const abs = Math.abs(amount);
  const remainder = abs % 10;

  if (remainder === 0) return amount;

  const roundedAbs = remainder <= 5
    ? abs - remainder
    : abs + (10 - remainder);

  return sign * roundedAbs;
}

const noBrowserSuggestInputProps = {
  autoComplete: 'off',
  autoCorrect: 'off',
  autoCapitalize: 'off',
  spellCheck: false,
  'data-lpignore': 'true',
  'data-1p-ignore': 'true',
  'data-form-type': 'other',
};

function normalizeKey(raw) {
  return String(raw || '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
}

function asBool(value, fallback = false) {
  if (typeof value === 'boolean') return value;
  if (value === 1 || value === '1' || value === 'true' || value === 'TRUE') return true;
  if (value === 0 || value === '0' || value === 'false' || value === 'FALSE') return false;
  return fallback;
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function hasValidNumericId(value) {
  const n = Number(value);
  return Number.isInteger(n) && n > 0;
}

function getSelectionValue(item) {
  if (!item) return '';
  if (hasValidNumericId(item.relationId)) return String(Number(item.relationId));
  if (hasValidNumericId(item.id)) return String(Number(item.id));
  return String(item.key || '');
}

function isCashMethod(method) {
  if (!method) return false;
  return !!method.permiteVuelto || normalizeKey(method.key) === 'EFECTIVO';
}

function isDebitCardMethod(method) {
  if (!method) return false;
  const k = normalizeKey(method.key);
  return k === 'TARJETA_DEBITO' || k === 'T_DEBITO';
}

function isCreditCardMethod(method) {
  if (!method) return false;
  const k = normalizeKey(method.key);
  return k === 'TARJETA_CREDITO' || k === 'T_CREDITO';
}

function isCreditCondition(method) {
  if (!method) return false;
  return normalizeKey(method.conditionKey) === 'CREDITO';
}

function requiresVoucher(method) {
  if (!method) return false;
  return !!method.requiereReferencia;
}

function getMethodIcon(method) {
  if (isCashMethod(method)) return AccountBalanceWalletIcon;
  if (isDebitCardMethod(method) || isCreditCardMethod(method)) return CreditCardIcon;
  if (isCreditCondition(method)) return HourglassBottomIcon;
  if (requiresVoucher(method)) return ReceiptLongIcon;
  return PaymentIcon;
}

function isAcceptedPosMethod(row) {
  const activo = asBool(row?.activo, true);
  const permitido = asBool(row?.permitido, true);

  const visiblePos =
    row?.visiblePos !== undefined
      ? asBool(row.visiblePos, false)
      : row?.visible_pos !== undefined
        ? asBool(row.visible_pos, false)
        : false;

  const configuracionCompleta =
    row?.configuracionCompleta !== undefined
      ? asBool(row.configuracionCompleta, false)
      : row?.configuracion_completa !== undefined
        ? asBool(row.configuracion_completa, false)
        : false;

  return activo && permitido && visiblePos && configuracionCompleta;
}

function buildMethods(rows) {
  const source = Array.isArray(rows) ? rows : [];

  const normalized = source
    .filter(isAcceptedPosMethod)
    .map((row, index) => {
      const relationId =
        row?.relationId ??
        row?.relation_id ??
        row?.relacion_id ??
        row?.id ??
        null;

      const methodId =
        row?.methodId ??
        row?.method_id ??
        row?.metodopago_id ??
        null;

      const conditionId =
        row?.conditionId ??
        row?.condition_id ??
        row?.condicionpago_id ??
        null;

      const key = normalizeKey(row?.key ?? row?.metodopago_clave ?? row?.clave);

      const conditionKey = normalizeKey(row?.conditionKey ?? row?.condicionpago_clave);

      if (!key || !hasValidNumericId(relationId)) return null;

      const label =
        row?.label ??
        row?.metodopago_nombre ??
        row?.nombre ??
        key;

      const conditionLabel =
        row?.conditionLabel ??
        row?.condicionpago_nombre ??
        row?.condicion_nombre ??
        conditionKey;

      const description =
        row?.description ??
        row?.metodopago_descripcion ??
        row?.descripcion ??
        '';

      const order = Number.isFinite(Number(row?.orden ?? row?.order))
        ? Number(row?.orden ?? row?.order)
        : index + 1;

      const item = {
        relationId: Number(relationId),
        id: Number(relationId),
        methodId: hasValidNumericId(methodId) ? Number(methodId) : null,
        conditionId: hasValidNumericId(conditionId) ? Number(conditionId) : null,

        key,
        conditionKey,

        label,
        conditionLabel,
        description,
        order,

        permitido: asBool(row?.permitido, true),
        activo: asBool(row?.activo, true),

        visiblePos:
          row?.visiblePos !== undefined
            ? asBool(row?.visiblePos, false)
            : asBool(row?.visible_pos, false),

        configuracionCompleta:
          row?.configuracionCompleta !== undefined
            ? asBool(row?.configuracionCompleta, false)
            : asBool(row?.configuracion_completa, false),

        requiereReferencia:
          row?.requiereReferencia !== undefined
            ? asBool(row?.requiereReferencia, false)
            : asBool(row?.requiere_referencia, false),

        requiereConfirmacion:
          row?.requiereConfirmacion !== undefined
            ? asBool(row?.requiereConfirmacion, false)
            : asBool(row?.requiere_confirmacion, false),

        requiereLiquidacion:
          row?.requiereLiquidacion !== undefined
            ? asBool(row?.requiereLiquidacion, false)
            : asBool(row?.requiere_liquidacion, false),

        permiteVuelto:
          row?.permiteVuelto !== undefined
            ? asBool(row?.permiteVuelto, false)
            : asBool(row?.permite_vuelto, false),

        usaTerminal:
          row?.usaTerminal !== undefined
            ? asBool(row?.usaTerminal, false)
            : asBool(row?.usa_terminal, false),

        usaPasarela:
          row?.usaPasarela !== undefined
            ? asBool(row?.usaPasarela, false)
            : asBool(row?.usa_pasarela, false),

        usaCuentaBancaria:
          row?.usaCuentaBancaria !== undefined
            ? asBool(row?.usaCuentaBancaria, false)
            : asBool(row?.usa_cuenta_bancaria, false),

        cuentaId: row?.cuenta_id ?? null,
        cuentaNombre: row?.cuenta_nombre ?? null,

        selectionValue: '',
        Icon: PaymentIcon,
      };

      item.selectionValue = getSelectionValue(item);
      item.Icon = getMethodIcon(item);

      return item;
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      if (a.label !== b.label) return a.label.localeCompare(b.label, 'es');
      return String(a.conditionLabel || '').localeCompare(String(b.conditionLabel || ''), 'es');
    });

  const seen = new Set();

  return normalized.filter((item) => {
    const dedupeKey = String(item.relationId);
    if (seen.has(dedupeKey)) return false;
    seen.add(dedupeKey);
    return true;
  });
}

function findSelectedMethod(methods, selectedValue) {
  const selected = String(selectedValue || '').trim();
  if (!selected) return null;

  return (
    methods.find((m) => m.selectionValue === selected) ||
    methods.find((m) => String(m.relationId) === selected) ||
    methods.find((m) => m.key === normalizeKey(selected)) ||
    null
  );
}

function normalizePreviousPayment(previousPayment, totalPayable = 0, labelFallback = '') {
  const p = previousPayment && typeof previousPayment === 'object' ? previousPayment : {};
  const totalVenta = toNumber(totalPayable, 0);

  const totalPagado = toNumber(
    p.total_pagado ??
      p.totalPagado ??
      p.monto_pagado ??
      p.montoPagado ??
      p.monto_confirmado ??
      p.monto ??
      p.total ??
      0,
    0
  );

  const saldo = toNumber(
    p.saldo ??
      p.saldo_pendiente ??
      p.saldoPendiente ??
      (totalVenta > 0 ? Math.max(totalVenta - totalPagado, 0) : 0),
    0
  );

  const movimientos = Array.isArray(p.movimientos)
    ? p.movimientos
    : Array.isArray(p.pagos)
      ? p.pagos
      : [];

  const metodo =
    p.metodo_pago_nombre ||
    p.metodo_pago_valor ||
    p.metodo_pago ||
    p.forma_pago ||
    p.payment_method ||
    labelFallback ||
    'Pago anterior';

  const metodoClave =
    p.metodo_pago_clave ||
    p.metodo_pago_key ||
    p.forma_pago_clave ||
    p.payment_method_type ||
    p.metodo_pago ||
    null;

  const referencia =
    p.referencia ||
    p.referencia_externa ||
    p.comprobante ||
    p.voucher_number ||
    movimientos?.[0]?.referencia ||
    movimientos?.[0]?.comprobante ||
    null;

  const fuente =
    p.fuente ||
    p.source ||
    p.fuente_pago ||
    p.origen_tipo ||
    'pedido';

  const origenTipo =
    p.origen_tipo ||
    p.tipo ||
    fuente ||
    'pedido';

  const origenId =
    p.origen_id ??
    p.id ??
    p.pedido_id ??
    p.source_id ??
    null;

  const existe =
    p.existe === true ||
    p.exists === true ||
    totalPagado > 0 ||
    movimientos.length > 0;

  const pagada =
    p.pagada === true ||
    p.paid === true ||
    p.estado_pago === 'pagada' ||
    p.estado_pago === 'paid' ||
    p.estado === 'pagada' ||
    (totalVenta > 0 && totalPagado >= totalVenta - 0.5 && saldo <= 0.5);

  return {
    existe,
    pagada,
    total_venta: totalVenta,
    total_pagado: totalPagado,
    saldo,
    metodo,
    metodo_clave: metodoClave,
    referencia,
    fuente,
    origen_tipo: origenTipo,
    origen_id: origenId,
    movimientos,
    raw: p,
  };
}

function formatDateTime(value) {
  if (!value) return '';

  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);

    return d.toLocaleString('es-CL', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return String(value);
  }
}

function normalizeRuleAdjustments(rulesEvaluation) {
  const ajustes = Array.isArray(rulesEvaluation?.ajustes)
    ? rulesEvaluation.ajustes
    : [];

  return ajustes
    .map((ajuste, index) => {
      const monto = toNumber(ajuste?.monto, 0);

      return {
        id: ajuste?.regla_id || ajuste?.id || ajuste?.codigo || index,
        codigo: ajuste?.codigo || '',
        nombre: ajuste?.nombre || ajuste?.glosa || ajuste?.codigo || 'Regla POS',
        glosa: ajuste?.glosa || ajuste?.nombre || ajuste?.codigo || 'Regla POS',
        tipo_regla: ajuste?.tipo_regla || '',
        tipo_ajuste: ajuste?.tipo_ajuste || '',
        tipo_calculo: ajuste?.tipo_calculo || '',
        monto,
        detalle: Array.isArray(ajuste?.detalle) ? ajuste.detalle : [],
      };
    })
    .filter((ajuste) => ajuste.monto !== 0);
}

function normalizePendingRuleAuthorizations(rulesEvaluation) {
  return Array.isArray(rulesEvaluation?.reglasPendientesPin)
    ? rulesEvaluation.reglasPendientesPin
    : [];
}


const PaymentModal = ({
  open,
  onClose,
  paymentStep,
  onNextStep,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  voucherNumber,
  setVoucherNumber,
  cashGivenState,
  setCashGiven,
  totalPayable,
  fiscalTotalPayable = null,
  canContinueDebit,
  canContinueCash,
  confirmPaymentDebit,
  confirmPaymentCash,
  confirmMixedPayment = null,
  paymentMethodsConfig = [],

  totalRulesAdjustment = 0,
  rulesEvaluation = null,

  hasPreviousPayment = false,
  previousPayment = null,
  previousPaymentLabel = '',
  previousPaymentSource = null,
  confirmPreviousPayment = null,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [showNormalFlowAfterPrevious, setShowNormalFlowAfterPrevious] = useState(false);
  const [payments, setPayments] = useState([]);
  const [currentAmount, setCurrentAmount] = useState('');
  const [flowError, setFlowError] = useState('');

  const methods = useMemo(() => buildMethods(paymentMethodsConfig), [paymentMethodsConfig]);
  const hasEnabledMethods = methods.length > 0;

  const fiscalTotal = useMemo(
    () => Math.max(
      0,
      Math.round(
        Number(
          fiscalTotalPayable !== null && fiscalTotalPayable !== undefined
            ? fiscalTotalPayable
            : totalPayable
        ) || 0
      )
    ),
    [fiscalTotalPayable, totalPayable]
  );

  const ruleAdjustments = useMemo(
    () => normalizeRuleAdjustments(rulesEvaluation),
    [rulesEvaluation]
  );

  const pendingRuleAuthorizations = useMemo(
    () => normalizePendingRuleAuthorizations(rulesEvaluation),
    [rulesEvaluation]
  );

  const rulesAdjustmentAmount = useMemo(() => {
    if (ruleAdjustments.length > 0) {
      return ruleAdjustments.reduce((sum, ajuste) => sum + toNumber(ajuste.monto, 0), 0);
    }

    return toNumber(totalRulesAdjustment, 0);
  }, [ruleAdjustments, totalRulesAdjustment]);

  const originalTotalBeforeRules = useMemo(
    () => Math.max(0, fiscalTotal - rulesAdjustmentAmount),
    [fiscalTotal, rulesAdjustmentAmount]
  );

  const selectedMethodMeta = useMemo(
    () => findSelectedMethod(methods, selectedPaymentMethod),
    [methods, selectedPaymentMethod]
  );

  const previousPaymentInfo = useMemo(
    () => normalizePreviousPayment(previousPayment, totalPayable, previousPaymentLabel),
    [previousPayment, totalPayable, previousPaymentLabel]
  );

  const previousPaymentFlowActive =
    open &&
    hasPreviousPayment &&
    previousPaymentInfo.existe &&
    !showNormalFlowAfterPrevious;

  const previousPaymentCoversTotal =
    previousPaymentInfo.pagada ||
    (
      Number(previousPaymentInfo.total_venta || 0) > 0 &&
      Number(previousPaymentInfo.total_pagado || 0) >= Number(previousPaymentInfo.total_venta || 0) - 0.5 &&
      Number(previousPaymentInfo.saldo || 0) <= 0.5
    );

  const appliedTotal = useMemo(
    () => payments.reduce((sum, payment) => sum + Math.round(Number(payment.amount_applied || 0)), 0),
    [payments]
  );

  const remaining = Math.max(0, fiscalTotal - appliedTotal);

  /*
   * Pago secuencial / mixto:
   * - un método solo puede utilizarse una vez por venta;
   * - si se usa efectivo en un pago mixto, debe ser el primer tramo;
   * - después del primer pago solo se ofrecen medios distintos de efectivo.
   *
   * Esto replica la regla operativa del POS: primero se recibe el efectivo
   * que el cliente desea entregar y el saldo exacto se completa con
   * Transbank, transferencia u otro medio configurado.
   */
  const getAvailableMethodsForSequence = useCallback((sequence = []) => {
    const usedRelations = new Set(
      sequence
        .map((payment) => Number(payment?.payment_relation_id || 0))
        .filter((id) => Number.isInteger(id) && id > 0)
    );

    const usedMethodIds = new Set(
      sequence
        .map((payment) => Number(payment?.payment_method_id || 0))
        .filter((id) => Number.isInteger(id) && id > 0)
    );

    const usedMethodKeys = new Set(
      sequence
        .map((payment) => normalizeKey(payment?.payment_method_key))
        .filter(Boolean)
    );

    return methods.filter((method) => {
      if (usedRelations.has(Number(method.relationId || 0))) return false;
      if (usedMethodIds.has(Number(method.methodId || 0))) return false;
      if (usedMethodKeys.has(normalizeKey(method.key))) return false;

      // En una secuencia mixta el efectivo solamente puede ser el primer tramo.
      if (sequence.length > 0 && isCashMethod(method)) return false;

      return true;
    });
  }, [methods]);

  const availableSequenceMethods = useMemo(
    () => getAvailableMethodsForSequence(payments),
    [getAvailableMethodsForSequence, payments]
  );

  const operatorSequenceMethods = useMemo(() => {
    const list = [...availableSequenceMethods];

    if (payments.length === 0) {
      list.sort((a, b) => {
        const cashOrder = Number(!isCashMethod(a)) - Number(!isCashMethod(b));
        if (cashOrder !== 0) return cashOrder;
        if (a.order !== b.order) return a.order - b.order;
        return String(a.label || '').localeCompare(String(b.label || ''), 'es');
      });
    }

    return list;
  }, [availableSequenceMethods, payments.length]);

  const isCash = isCashMethod(selectedMethodMeta);
  const isCredit = isCreditCondition(selectedMethodMeta);
  const showsReferenceField = requiresVoucher(selectedMethodMeta);

  const methodLabel = selectedMethodMeta?.label || 'Pago';
  const conditionLabel = selectedMethodMeta?.conditionLabel || '';
  const fullMethodLabel = [methodLabel, conditionLabel].filter(Boolean).join(' · ');

  const cashGiven = Math.max(0, parseInt(cashGivenState || '0', 10) || 0);
  const cashTargetForRemaining = roundCashLegCLP(remaining);
  const cashClosesRemaining =
    remaining > 0 &&
    cashGiven > 0 &&
    cashGiven >= cashTargetForRemaining;

  /*
   * Si el efectivo NO completa el saldo, todo lo recibido se aplica al pago
   * y el vuelto es siempre $0. Como el backend redondea cada tramo efectivo
   * según la regla CLP, exigimos múltiplos de $10 para un tramo parcial.
   * De ese modo amount_applied === amount_paid === amount_received y no se
   * genera ni redondeo ni vuelto artificial.
   */
  const cashIsPartial = cashGiven > 0 && !cashClosesRemaining;
  const cashPartialAmountInvalid = cashIsPartial && (cashGiven % 10 !== 0);

  const cashCurrentApplied = cashClosesRemaining
    ? remaining
    : Math.min(cashGiven, remaining);

  const cashCurrentPaid = cashClosesRemaining
    ? cashTargetForRemaining
    : cashCurrentApplied;

  const cashCurrentChange = cashClosesRemaining
    ? Math.max(cashGiven - cashCurrentPaid, 0)
    : 0;

  const cashCurrentRounding = cashClosesRemaining
    ? cashCurrentPaid - cashCurrentApplied
    : 0;

  const nonCashCurrentAmount = useMemo(() => {
    const parsed = Math.round(Number(currentAmount || 0));
    if (!Number.isFinite(parsed)) return 0;
    return Math.max(0, Math.min(parsed, remaining));
  }, [currentAmount, remaining]);

  const currentApplied = isCash ? cashCurrentApplied : nonCashCurrentAmount;
  const currentRemainingAfter = Math.max(0, remaining - currentApplied);
  const currentCompletesSale = remaining > 0 && currentApplied >= remaining;

  const resetSequence = useCallback(() => {
    setPayments([]);
    setFlowError('');
    setVoucherNumber?.('');
    setCashGiven?.('');
    setCurrentAmount(String(fiscalTotal));
    onNextStep?.(0);
  }, [fiscalTotal, onNextStep, setCashGiven, setVoucherNumber]);

  useEffect(() => {
    if (!open) {
      setSubmitting(false);
      setShowNormalFlowAfterPrevious(false);
      setPayments([]);
      setCurrentAmount('');
      setFlowError('');
      return;
    }

    setCurrentAmount(String(fiscalTotal));
    setFlowError('');
  }, [open, fiscalTotal]);

  useEffect(() => {
    if (!open) return;
    if (previousPaymentFlowActive) return;
    if (!hasEnabledMethods) return;

    if (!selectedMethodMeta) {
      const cashMethod =
        methods.find((method) => isCashMethod(method)) ||
        methods.find((method) => normalizeKey(method?.key) === 'EFECTIVO') ||
        methods[0] ||
        null;

      if (cashMethod) {
        setSelectedPaymentMethod(cashMethod.selectionValue);
      }
    }
  }, [
    open,
    previousPaymentFlowActive,
    hasEnabledMethods,
    methods,
    selectedMethodMeta,
    setSelectedPaymentMethod,
  ]);

  const buildPayment = useCallback((method) => {
    if (!method) return null;

    const reference = String(voucherNumber || '').trim() || null;

    if (isCashMethod(method)) {
      if (cashCurrentApplied <= 0) return null;

      return {
        payment_relation_id: method.relationId || null,
        payment_method_id: method.methodId || null,
        payment_method_key: method.key || null,
        payment_method_name: method.label || null,
        payment_condition_id: method.conditionId || null,
        payment_condition_key: method.conditionKey || null,
        amount_applied: cashCurrentApplied,
        amount_paid: cashCurrentPaid,
        amount_received: cashGiven,
        change_given: cashCurrentChange,
        cash_rounding: cashCurrentRounding,
        voucher_number: null,
        reference: null,
        referencia: null,
      };
    }

    if (nonCashCurrentAmount <= 0) return null;

    return {
      payment_relation_id: method.relationId || null,
      payment_method_id: method.methodId || null,
      payment_method_key: method.key || null,
      payment_method_name: method.label || null,
      payment_condition_id: method.conditionId || null,
      payment_condition_key: method.conditionKey || null,
      amount_applied: nonCashCurrentAmount,
      amount_paid: nonCashCurrentAmount,
      amount_received: nonCashCurrentAmount,
      change_given: 0,
      cash_rounding: 0,
      voucher_number: reference,
      reference,
      referencia: reference,
    };
  }, [
    voucherNumber,
    cashCurrentApplied,
    cashCurrentPaid,
    cashGiven,
    cashCurrentChange,
    cashCurrentRounding,
    nonCashCurrentAmount,
  ]);

  const selectNextMethodAndOpen = useCallback((currentMethod, nextRemaining, nextSequence) => {
    const candidates = getAvailableMethodsForSequence(nextSequence || []);

    if (!candidates.length) {
      setSelectedPaymentMethod('');
      setVoucherNumber?.('');
      setCashGiven?.('');
      setCurrentAmount(String(Math.max(0, Math.round(nextRemaining))));
      onNextStep?.(0);
      setFlowError(
        'Queda saldo pendiente, pero no hay otro medio de pago disponible. Reinicie la secuencia o habilite otro método POS.'
      );
      return false;
    }

    const currentIndex = Math.max(
      0,
      methods.findIndex((method) => method.selectionValue === currentMethod?.selectionValue)
    );

    let nextMethod = null;

    // Respeta el orden configurado, saltando métodos ya usados y efectivo
    // cuando la secuencia ya comenzó.
    for (let offset = 1; offset <= methods.length; offset += 1) {
      const candidate = methods[(currentIndex + offset) % methods.length];
      if (candidates.some((item) => item.selectionValue === candidate.selectionValue)) {
        nextMethod = candidate;
        break;
      }
    }

    nextMethod = nextMethod || candidates[0];

    setSelectedPaymentMethod(nextMethod.selectionValue);
    setVoucherNumber?.('');
    setCashGiven?.('');
    setCurrentAmount(String(Math.max(0, Math.round(nextRemaining))));

    const nextStep = isCashMethod(nextMethod) ? 2 : 1;
    onNextStep?.(nextStep);

    setTimeout(() => {
      if (isCashMethod(nextMethod)) {
        document.getElementById('cash-input')?.focus();
      } else {
        const targetId = requiresVoucher(nextMethod)
          ? 'voucher-input'
          : 'payment-amount-input';
        document.getElementById(targetId)?.focus();
      }
    }, 40);

    return true;
  }, [
    getAvailableMethodsForSequence,
    methods,
    onNextStep,
    setCashGiven,
    setSelectedPaymentMethod,
    setVoucherNumber,
  ]);

  const submitSequence = useCallback(async (nextPayments) => {
    if (typeof confirmMixedPayment !== 'function') {
      setFlowError(
        'Este PaymentModal necesita la prop confirmMixedPayment para finalizar pagos con más de un medio.'
      );
      return;
    }

    setSubmitting(true);
    setFlowError('');

    try {
      await Promise.resolve(confirmMixedPayment(nextPayments));
    } finally {
      setSubmitting(false);
    }
  }, [confirmMixedPayment]);

  const registerCurrentPayment = useCallback(async () => {
    if (submitting || !selectedMethodMeta || remaining <= 0) return;

    setFlowError('');

    if (isCashMethod(selectedMethodMeta) && cashPartialAmountInvalid) {
      setFlowError(
        'Para un pago parcial en efectivo ingrese un monto múltiplo de $10. El efectivo parcial se aplica completo y no genera vuelto.'
      );
      setTimeout(() => document.getElementById('cash-input')?.focus(), 40);
      return;
    }

    const payment = buildPayment(selectedMethodMeta);

    if (!payment || Number(payment.amount_applied || 0) <= 0) {
      setFlowError('Ingrese un monto mayor a $0 para continuar.');
      return;
    }

    const nextPayments = [...payments, payment];
    const nextApplied = nextPayments.reduce(
      (sum, item) => sum + Math.round(Number(item.amount_applied || 0)),
      0
    );
    const nextRemaining = Math.max(0, fiscalTotal - nextApplied);

    if (nextRemaining <= 0) {
      // Conserva exactamente el flujo simple anterior cuando un solo medio cubre todo.
      if (payments.length === 0) {
        setSubmitting(true);

        try {
          if (isCashMethod(selectedMethodMeta)) {
            await Promise.resolve(confirmPaymentCash?.());
          } else {
            await Promise.resolve(confirmPaymentDebit?.());
          }
        } finally {
          setSubmitting(false);
        }

        return;
      }

      await submitSequence(nextPayments);
      return;
    }

    if (isCreditCondition(selectedMethodMeta)) {
      setFlowError('Una condición de crédito no puede registrarse como tramo parcial dentro de esta secuencia.');
      return;
    }

    setPayments(nextPayments);
    selectNextMethodAndOpen(selectedMethodMeta, nextRemaining, nextPayments);
  }, [
    submitting,
    selectedMethodMeta,
    remaining,
    voucherNumber,
    cashPartialAmountInvalid,
    buildPayment,
    payments,
    fiscalTotal,
    confirmPaymentCash,
    confirmPaymentDebit,
    submitSequence,
    selectNextMethodAndOpen,
  ]);

  const openPaymentMethod = useCallback((method) => {
    if (!method || submitting) return;

    const available = getAvailableMethodsForSequence(payments);
    const methodAvailable = available.some(
      (item) => item.selectionValue === method.selectionValue
    );

    if (!methodAvailable) {
      setFlowError(
        isCashMethod(method) && payments.length > 0
          ? 'El efectivo debe registrarse primero. Seleccione otro medio para completar el saldo.'
          : 'Este medio de pago ya fue utilizado. Seleccione otro.'
      );
      return;
    }

    setSelectedPaymentMethod(method.selectionValue);
    setFlowError('');
    setCurrentAmount(String(remaining));

    if (isCashMethod(method)) {
      setCashGiven?.('');
      setVoucherNumber?.('');
      onNextStep?.(2);
      setTimeout(() => document.getElementById('cash-input')?.focus(), 40);
      return;
    }

    setCashGiven?.('');
    setVoucherNumber?.('');
    onNextStep?.(1);

    setTimeout(() => {
      const targetId = requiresVoucher(method)
        ? 'voucher-input'
        : 'payment-amount-input';
      document.getElementById(targetId)?.focus();
    }, 40);
  }, [
    submitting,
    payments,
    getAvailableMethodsForSequence,
    remaining,
    onNextStep,
    setCashGiven,
    setSelectedPaymentMethod,
    setVoucherNumber,
  ]);

  const goToSelectedMethod = useCallback(() => {
    if (!selectedMethodMeta) return;
    openPaymentMethod(selectedMethodMeta);
  }, [selectedMethodMeta, openPaymentMethod]);

  const goBackToMethods = useCallback(() => {
    if (submitting) return;
    setFlowError('');
    setVoucherNumber?.('');
    setCashGiven?.('');
    setCurrentAmount(String(remaining));
    onNextStep?.(0);
  }, [submitting, remaining, onNextStep, setCashGiven, setVoucherNumber]);

  const confirmPreviousPaymentSafe = useCallback(async () => {
    if (submitting) return;

    if (!previousPaymentCoversTotal) {
      setShowNormalFlowAfterPrevious(true);
      onNextStep?.(0);
      return;
    }

    setSubmitting(true);

    try {
      if (typeof confirmPreviousPayment === 'function') {
        await Promise.resolve(confirmPreviousPayment(previousPaymentInfo));
      } else {
        await Promise.resolve(confirmPaymentDebit?.());
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    submitting,
    previousPaymentCoversTotal,
    confirmPreviousPayment,
    previousPaymentInfo,
    confirmPaymentDebit,
    onNextStep,
  ]);

  const handleGoToNormalPayment = useCallback(() => {
    setShowNormalFlowAfterPrevious(true);
    resetSequence();
  }, [resetSequence]);

  useEffect(() => {
    if (!open || previousPaymentFlowActive || paymentStep !== 0) return;
    if (!operatorSequenceMethods.length) return;

    const selectedStillAvailable = availableSequenceMethods.some(
      (method) => method.selectionValue === selectedMethodMeta?.selectionValue
    );

    if (!selectedStillAvailable) {
      const preferred = payments.length === 0
        ? (availableSequenceMethods.find((method) => isCashMethod(method)) || availableSequenceMethods[0])
        : availableSequenceMethods[0];

      setSelectedPaymentMethod(preferred.selectionValue);
    }
  }, [
    open,
    previousPaymentFlowActive,
    paymentStep,
    payments.length,
    availableSequenceMethods,
    operatorSequenceMethods,
    selectedMethodMeta,
    setSelectedPaymentMethod,
  ]);

  const renderRulesPaymentSummary = () => {
    const hasRules =
      ruleAdjustments.length > 0 ||
      pendingRuleAuthorizations.length > 0 ||
      rulesAdjustmentAmount !== 0;

    if (!hasRules) return null;

    return (
      <Box
        sx={{
          mb: 1.25,
          px: 1.25,
          py: 0.9,
          border: '1px solid',
          borderColor: pendingRuleAuthorizations.length ? 'warning.light' : 'success.light',
          borderRadius: 2,
          bgcolor: pendingRuleAuthorizations.length ? '#fff8e1' : '#f6fbf2',
        }}
      >
        <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="center">
          <Stack direction="row" spacing={0.75} alignItems="center">
            <LocalOfferIcon
              fontSize="small"
              color={pendingRuleAuthorizations.length ? 'warning' : 'success'}
            />
            <Typography variant="body2" fontWeight={900}>
              Reglas POS
            </Typography>
          </Stack>

          <Typography
            variant="body2"
            fontWeight={900}
            color={rulesAdjustmentAmount < 0 ? 'success.main' : rulesAdjustmentAmount > 0 ? 'error.main' : 'text.primary'}
          >
            {rulesAdjustmentAmount > 0 ? '+' : ''}{fmtCLP(rulesAdjustmentAmount)}
          </Typography>
        </Stack>

        <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.45 }}>
          <Typography variant="caption" color="text.secondary">
            Original {fmtCLP(originalTotalBeforeRules)}
          </Typography>
          <Typography variant="caption" fontWeight={900}>
            Total {fmtCLP(fiscalTotal)}
          </Typography>
        </Stack>

        {pendingRuleAuthorizations.length > 0 && (
          <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.35 }}>
            Hay regla(s) pendientes de autorización por PIN.
          </Typography>
        )}
      </Box>
    );
  };

  const renderPaymentProgress = () => {
    if (!payments.length) return null;

    return (
      <Box
        sx={{
          mb: 1.25,
          px: 1.25,
          py: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Typography variant="body2" fontWeight={900}>
            Pago parcial
          </Typography>

          <Button
            size="small"
            color="inherit"
            onClick={resetSequence}
            disabled={submitting}
          >
            Reiniciar
          </Button>
        </Stack>

        <Stack
          direction="row"
          spacing={0.75}
          useFlexGap
          flexWrap="wrap"
          sx={{ mt: 0.75 }}
        >
          {payments.map((payment, index) => (
            <Chip
              key={`${payment.payment_relation_id || payment.payment_method_key || 'pago'}-${index}`}
              size="small"
              color="success"
              variant="outlined"
              label={`${payment.payment_method_name || payment.payment_method_key || `Pago ${index + 1}`} ${fmtCLP(payment.amount_applied)}`}
            />
          ))}
        </Stack>

        <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Saldo por cobrar
          </Typography>
          <Typography variant="h6" fontWeight={900} color="primary.main">
            {fmtCLP(remaining)}
          </Typography>
        </Stack>
      </Box>
    );
  };

  const canRegisterCurrent = useMemo(() => {
    if (submitting || !selectedMethodMeta || remaining <= 0) return false;

    if (isCash) {
      return cashGiven > 0 && cashCurrentApplied > 0 && !cashPartialAmountInvalid;
    }

    return nonCashCurrentAmount > 0;
  }, [
    submitting,
    selectedMethodMeta,
    remaining,
    isCash,
    cashGiven,
    cashCurrentApplied,
    cashPartialAmountInvalid,
    nonCashCurrentAmount,
  ]);

  const actionLabel = currentCompletesSale
    ? (
        isCash && cashCurrentChange > 0
          ? `Cobrar ${fmtCLP(cashCurrentPaid)} · vuelto ${fmtCLP(cashCurrentChange)}`
          : `Cobrar ${fmtCLP(currentApplied)} y finalizar`
      )
    : `Aplicar ${fmtCLP(currentApplied)} · queda ${fmtCLP(currentRemainingAfter)}`;

  const handleKeyDown = useCallback((e) => {
    if (!open) return;

    if (previousPaymentFlowActive) {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (previousPaymentCoversTotal) {
          confirmPreviousPaymentSafe();
        } else {
          handleGoToNormalPayment();
        }
      }
      return;
    }

    if (!hasEnabledMethods) return;

    if (paymentStep === 0) {
      if (/^[1-9]$/.test(e.key)) {
        const shortcutIndex = Number(e.key) - 1;
        const shortcutMethod = operatorSequenceMethods[shortcutIndex];

        if (shortcutMethod) {
          e.preventDefault();
          openPaymentMethod(shortcutMethod);
        }
        return;
      }

      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();

        if (!operatorSequenceMethods.length) return;

        const idx = operatorSequenceMethods.findIndex(
          (method) => method.selectionValue === selectedMethodMeta?.selectionValue
        );
        const safeIdx = idx >= 0 ? idx : 0;
        const nextIdx = e.key === 'ArrowRight'
          ? (safeIdx + 1) % operatorSequenceMethods.length
          : (safeIdx - 1 + operatorSequenceMethods.length) % operatorSequenceMethods.length;

        setSelectedPaymentMethod(operatorSequenceMethods[nextIdx].selectionValue);
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        goToSelectedMethod();
      }

      return;
    }

    if (paymentStep === 1 && !isCash) {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (canRegisterCurrent) registerCurrentPayment();
      }
      return;
    }

    if (paymentStep === 2 && isCash) {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setCashGiven?.(String(cashGiven + 1000));
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setCashGiven?.(String(Math.max(0, cashGiven - 1000)));
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        if (canRegisterCurrent) registerCurrentPayment();
      }
    }
  }, [
    open,
    previousPaymentFlowActive,
    previousPaymentCoversTotal,
    confirmPreviousPaymentSafe,
    handleGoToNormalPayment,
    hasEnabledMethods,
    paymentStep,
    availableSequenceMethods,
    operatorSequenceMethods,
    selectedMethodMeta,
    setSelectedPaymentMethod,
    goToSelectedMethod,
    openPaymentMethod,
    isCash,
    canRegisterCurrent,
    registerCurrentPayment,
    cashGiven,
    setCashGiven,
  ]);

  useEffect(() => {
    if (!open) return undefined;

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, handleKeyDown]);

  const title = previousPaymentFlowActive ? 'Pago anterior detectado' : 'Cobrar venta';

  const handleDialogClose = useCallback((event, reason) => {
    if (submitting) return;
    if (reason === 'backdropClick') return;
    onClose?.();
  }, [submitting, onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown={submitting}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      PaperProps={{
        component: 'form',
        autoComplete: 'off',
        onSubmit: (event) => event.preventDefault(),
        sx: {
          maxHeight: '92vh',
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1,
          px: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 900 }}>
          {title}
        </Typography>

        <IconButton size="small" onClick={onClose} disabled={submitting}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 2, py: 1.5 }}>
        {previousPaymentFlowActive && (
          <Box>
            <Alert
              severity={previousPaymentCoversTotal ? 'success' : 'warning'}
              icon={previousPaymentCoversTotal ? <CheckCircleIcon /> : <WarningAmberIcon />}
              sx={{ mb: 1.25, py: 0.75 }}
            >
              {previousPaymentCoversTotal
                ? 'Pedido pagado. Puede emitir la venta sin registrar un nuevo pago.'
                : 'Pago anterior parcial. Revise el saldo antes de continuar.'}
            </Alert>

            {renderRulesPaymentSummary()}

            <Box
              sx={{
                border: '1px solid',
                borderColor: previousPaymentCoversTotal ? 'success.light' : 'warning.light',
                borderRadius: 2,
                p: 1.25,
                bgcolor: previousPaymentCoversTotal ? '#f6fbf2' : '#fff8e1',
              }}
            >
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Typography variant="body2" fontWeight={900}>
                  {previousPaymentInfo.metodo || 'Pago anterior'}
                </Typography>
                <Chip
                  size="small"
                  color={previousPaymentCoversTotal ? 'success' : 'warning'}
                  label={previousPaymentCoversTotal ? 'Pagado' : 'Parcial'}
                />
              </Stack>

              <Stack spacing={0.5} sx={{ mt: 1 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">Total venta</Typography>
                  <Typography variant="caption" fontWeight={800}>
                    {fmtCLP(previousPaymentInfo.total_venta || fiscalTotal)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">Pago detectado</Typography>
                  <Typography variant="caption" fontWeight={900} color="success.main">
                    {fmtCLP(previousPaymentInfo.total_pagado)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" fontWeight={900}>Saldo</Typography>
                  <Typography variant="body2" fontWeight={900} color="warning.main">
                    {fmtCLP(previousPaymentInfo.saldo)}
                  </Typography>
                </Stack>
              </Stack>

              {previousPaymentInfo.referencia && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  Ref: {previousPaymentInfo.referencia}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {!previousPaymentFlowActive && (
          <>
            {renderRulesPaymentSummary()}
            {renderPaymentProgress()}

            {flowError && (
              <Alert severity="error" sx={{ mb: 1.25, py: 0.5 }}>
                {flowError}
              </Alert>
            )}
          </>
        )}

        {!previousPaymentFlowActive && paymentStep === 0 && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>
              {payments.length ? 'Saldo por cobrar' : 'Total por cobrar'}
            </Typography>
            <Typography
              variant="h4"
              fontWeight={900}
              color="primary.main"
              sx={{ lineHeight: 1.05, mb: 0.75 }}
            >
              {fmtCLP(remaining)}
            </Typography>
            <Typography variant="body2" fontWeight={800} sx={{ mb: 1.25 }}>
              Seleccione medio de pago
            </Typography>

            {!hasEnabledMethods ? (
              <Alert severity="warning" sx={{ mt: 1, textAlign: 'left' }}>
                No hay métodos de pago disponibles para POS.
              </Alert>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))',
                    gap: 1,
                    mt: 0.5,
                  }}
                >
                  {operatorSequenceMethods.map((method, index) => {
                    const active =
                      method.selectionValue === selectedMethodMeta?.selectionValue ||
                      String(selectedPaymentMethod || '') === method.selectionValue ||
                      String(selectedPaymentMethod || '') === String(method.relationId);

                    const Icon = method.Icon;

                    return (
                      <Box
                        key={method.selectionValue}
                        role="button"
                        tabIndex={0}
                        onClick={() => openPaymentMethod(method)}
                        sx={{
                          position: 'relative',
                          cursor: submitting ? 'wait' : 'pointer',
                          minHeight: 92,
                          p: 1.25,
                          borderRadius: 2,
                          border: '2px solid',
                          borderColor: active ? 'primary.main' : 'divider',
                          bgcolor: active ? 'primary.main' : 'background.paper',
                          color: active ? 'primary.contrastText' : 'text.primary',
                          boxShadow: active ? 3 : 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.5,
                          userSelect: 'none',
                          transition: 'transform 100ms ease, box-shadow 100ms ease',
                          '&:active': {
                            transform: 'scale(0.98)',
                          },
                        }}
                      >
                        {index < 9 && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 6,
                              left: 7,
                              minWidth: 22,
                              height: 22,
                              px: 0.5,
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 12,
                              fontWeight: 900,
                              bgcolor: active ? 'rgba(255,255,255,0.18)' : 'action.hover',
                            }}
                          >
                            {index + 1}
                          </Box>
                        )}

                        <Icon sx={{ fontSize: 34 }} />

                        <Typography variant="body1" fontWeight={900} sx={{ lineHeight: 1.1 }}>
                          {method.label}
                        </Typography>

                        {method.conditionLabel && method.conditionLabel !== method.label && (
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            {method.conditionLabel}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: 'block' }}
                >
                  Clic o teclas 1–9 · ← / → y Enter
                </Typography>
              </>
            )}
          </Box>
        )}

        {!previousPaymentFlowActive && paymentStep === 1 && !isCash && (
          <Box sx={{ textAlign: 'center', mt: 0.25 }}>
            <Typography variant="body2" color="text.secondary">
              Saldo por cobrar
            </Typography>
            <Typography variant="h4" fontWeight={900} sx={{ lineHeight: 1.05, mb: 0.5 }}>
              {fmtCLP(remaining)}
            </Typography>

            <Typography variant="h6" fontWeight={900} color="primary.main" sx={{ mb: 1.25 }}>
              {fullMethodLabel}
            </Typography>

            <TextField
              id="payment-amount-input"
              name="pos_payment_amount"
              value={currentAmount}
              onChange={(event) => {
                const clean = event.target.value.replace(/[^0-9]/g, '');
                setCurrentAmount(clean);
                setFlowError('');
              }}
              fullWidth
              label={`Monto con ${methodLabel}`}
              autoFocus={!showsReferenceField}
              disabled={submitting}
              inputProps={{
                ...noBrowserSuggestInputProps,
                inputMode: 'numeric',
                pattern: '[0-9]*',
                style: {
                  textAlign: 'right',
                  fontSize: 22,
                  fontWeight: 900,
                },
              }}
            />

            {nonCashCurrentAmount !== remaining && (
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setCurrentAmount(String(remaining));
                  setFlowError('');
                }}
                disabled={submitting}
                sx={{ mt: 0.25 }}
              >
                Usar saldo completo {fmtCLP(remaining)}
              </Button>
            )}

            {showsReferenceField && (
              <TextField
                id="voucher-input"
                name="pos_voucher_number"
                value={voucherNumber}
                onChange={(event) => {
                  setVoucherNumber(event.target.value);
                  setFlowError('');
                }}
                fullWidth
                label="N° operación / referencia"
                autoFocus
                autoComplete="off"
                disabled={submitting}
                inputProps={noBrowserSuggestInputProps}
                helperText="Debe ingresar un número de voucher o referencia."
                sx={{ mt: 1 }}
              />
            )}

            {nonCashCurrentAmount > 0 && nonCashCurrentAmount < remaining && (
              <Box
                sx={{
                  mt: 1.25,
                  p: 1,
                  borderRadius: 2,
                  bgcolor: 'action.hover',
                }}
              >
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography variant="body2">Aplicar ahora</Typography>
                  <Typography variant="body2" fontWeight={900}>
                    {fmtCLP(nonCashCurrentAmount)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mt: 0.35 }}>
                  <Typography variant="body2" color="text.secondary">Quedará pendiente</Typography>
                  <Typography variant="body2" fontWeight={900} color="primary.main">
                    {fmtCLP(currentRemainingAfter)}
                  </Typography>
                </Stack>
              </Box>
            )}

            {isCredit && (
              <Alert severity="info" sx={{ mt: 1, textAlign: 'left', py: 0.5 }}>
                Venta bajo condición <strong>{conditionLabel}</strong>.
              </Alert>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Enter confirma
            </Typography>
          </Box>
        )}

        {!previousPaymentFlowActive && paymentStep === 2 && isCash && (
          <Box sx={{ textAlign: 'center', mt: 0.25 }}>
            <Typography variant="body2" color="text.secondary">
              Saldo por cobrar
            </Typography>
            <Typography variant="h4" fontWeight={900} sx={{ lineHeight: 1.05, mb: 0.5 }}>
              {fmtCLP(remaining)}
            </Typography>

            <Typography variant="h6" fontWeight={900} color="success.main" sx={{ mb: 1 }}>
              Efectivo
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 0.75,
                mb: 1,
                opacity: submitting ? 0.7 : 1,
                pointerEvents: submitting ? 'none' : 'auto',
              }}
            >
              <Button
                onClick={() => {
                  setCashGiven(String(cashTargetForRemaining));
                  setFlowError('');
                  setTimeout(() => document.getElementById('cash-input')?.focus(), 20);
                }}
                variant="contained"
                color="success"
                sx={{
                  minWidth: 82,
                  minHeight: 42,
                  borderRadius: 1.5,
                  fontWeight: 900,
                }}
              >
                Exacto
              </Button>

              {bills.map(({ value, img }) => (
                <Button
                  key={value}
                  onClick={() => {
                    setCashGiven(String(cashGiven + value));
                    setFlowError('');
                  }}
                  variant="outlined"
                  sx={{
                    p: 0,
                    minWidth: 72,
                    minHeight: 42,
                    borderRadius: 1,
                  }}
                >
                  <img
                    src={img}
                    alt={`${value}`}
                    style={{ maxHeight: '100%', maxWidth: '100%' }}
                  />
                </Button>
              ))}
            </Box>

            <TextField
              id="cash-input"
              name="pos_cash_received"
              value={cashGivenState}
              onChange={(event) => {
                const clean = event.target.value.replace(/[^0-9]/g, '').replace(/^0+/, '');
                setCashGiven(clean);
                setFlowError('');
              }}
              fullWidth
              label="Efectivo recibido"
              autoFocus
              autoComplete="off"
              disabled={submitting}
              inputProps={{
                ...noBrowserSuggestInputProps,
                inputMode: 'numeric',
                pattern: '[0-9]*',
                style: {
                  textAlign: 'right',
                  fontSize: 24,
                  fontWeight: 900,
                },
              }}
            />

            {cashGiven <= 0 ? (
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                Ingrese el efectivo. Si es menor al saldo, se aplicará completo y continuará con otro medio.
              </Typography>
            ) : cashPartialAmountInvalid ? (
              <Alert severity="warning" sx={{ mt: 1, textAlign: 'left', py: 0.5 }}>
                El efectivo parcial debe ser múltiplo de $10.
              </Alert>
            ) : cashClosesRemaining ? (
              <Box
                sx={{
                  mt: 1.25,
                  p: 1.15,
                  borderRadius: 2,
                  bgcolor: cashCurrentChange > 0 ? '#f6fbf2' : 'action.hover',
                  border: '1px solid',
                  borderColor: cashCurrentChange > 0 ? 'success.light' : 'divider',
                }}
              >
                {cashCurrentChange > 0 ? (
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                    <Typography variant="body1" fontWeight={800}>Vuelto</Typography>
                    <Typography variant="h5" color="success.main" fontWeight={900}>
                      {fmtCLP(cashCurrentChange)}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="body1" color="success.main" fontWeight={900}>
                    Pago exacto
                  </Typography>
                )}

                {cashCurrentRounding !== 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                    Ajuste de redondeo: {cashCurrentRounding > 0 ? '+' : ''}{fmtCLP(cashCurrentRounding)}
                  </Typography>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  mt: 1.25,
                  p: 1.15,
                  borderRadius: 2,
                  bgcolor: 'action.hover',
                }}
              >
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography variant="body1">Aplicar efectivo</Typography>
                  <Typography variant="body1" fontWeight={900}>
                    {fmtCLP(cashCurrentApplied)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mt: 0.4 }}>
                  <Typography variant="body1" color="text.secondary">Quedará pendiente</Typography>
                  <Typography variant="h6" fontWeight={900} color="primary.main">
                    {fmtCLP(currentRemainingAfter)}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                  Sin vuelto
                </Typography>
              </Box>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              ↑ / ↓ suma o resta $1.000 · Enter confirma
            </Typography>
          </Box>
        )}

      </DialogContent>

      {previousPaymentFlowActive ? (
        <DialogActions sx={{ justifyContent: 'center', py: 1, px: 2, gap: 1 }}>
          <Button onClick={onClose} color="inherit" disabled={submitting} size="small">
            Cancelar
          </Button>

          {!previousPaymentCoversTotal && (
            <Button
              variant="outlined"
              color="primary"
              disabled={submitting}
              onClick={handleGoToNormalPayment}
              size="small"
            >
              Continuar pago normal
            </Button>
          )}

          {previousPaymentCoversTotal && (
            <Button
              variant="contained"
              color="success"
              disabled={submitting}
              onClick={confirmPreviousPaymentSafe}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
              size="small"
              sx={{ px: 3, fontWeight: 900 }}
            >
              {submitting ? 'Procesando…' : 'Emitir pagada'}
            </Button>
          )}
        </DialogActions>
      ) : (
        <DialogActions sx={{ py: 1.25, px: 2, gap: 1 }}>
          {paymentStep === 0 ? (
            <Button
              color="inherit"
              onClick={onClose}
              disabled={submitting}
              size="small"
              sx={{ mx: 'auto' }}
            >
              Cancelar
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                color="inherit"
                onClick={goBackToMethods}
                disabled={submitting}
                sx={{ minHeight: 50, px: 2 }}
              >
                Cambiar
              </Button>

              <Button
                variant="contained"
                color={currentCompletesSale ? 'success' : 'primary'}
                disabled={!canRegisterCurrent || submitting}
                onClick={registerCurrentPayment}
                sx={{
                  flex: 1,
                  minHeight: 50,
                  fontWeight: 900,
                  fontSize: 16,
                }}
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <PaymentIcon />}
              >
                {submitting ? 'Procesando…' : actionLabel}
              </Button>
            </>
          )}
        </DialogActions>
      )}

    </Dialog>
  );
};

export default PaymentModal;
